<template>
	<section>
		<loader :dialog="loading"></loader>
		<floating-alert
			:message="alertMessage"
			:show="alertShow"
			:type="alertType"
		></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- Header -->
			<div class="fl-header">
				<div class="fl-header-left">
					<div class="fl-icon-box">
						<v-icon size="18" color="white">mdi-food</v-icon>
					</div>
					<div>
						<p class="fl-title">Liste des repas</p>
						<p class="fl-subtitle">Gestion des repas disponibles</p>
					</div>
				</div>
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined
					dense
					hide-details
					class="fl-search"
					color="blue darken-2"
				></v-text-field>
				<v-dialog v-model="dialog" max-width="560px" scrollable>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							depressed
							rounded
							class="fl-add-btn ml-3"
							v-bind="attrs"
							v-on="on"
						>
							<v-icon left small>mdi-plus</v-icon>
							Nouveau
						</v-btn>
					</template>
					<v-card rounded="xl" class="pa-2">
						<div class="fl-dialog-header">
							<div class="fl-icon-box-sm">
								<v-icon size="16" color="white">mdi-food</v-icon>
							</div>
							<div>
								<p class="fl-dialog-title">{{ formTitle }}</p>
								<p class="fl-dialog-sub">Repas disponible</p>
							</div>
							<v-spacer></v-spacer>
							<v-btn icon small @click="close">
								<v-icon color="#6b7280">mdi-close</v-icon>
							</v-btn>
						</div>
						<v-divider></v-divider>
						<v-card-text class="pt-5">
							<v-row>
								<v-col cols="12">
									<v-text-field
										v-model="editedItem.label"
										label="Intitulé du repas"
										outlined
										dense
										rounded
										prepend-inner-icon="mdi-food"
										hide-details
									></v-text-field>
								</v-col>
								<v-col cols="12">
									<v-select
										:items="foodCategories"
										item-text="label"
										item-value="_id"
										v-model="editedItem.category"
										label="Catégorie"
										outlined
										dense
										rounded
										prepend-inner-icon="mdi-folder"
										hide-details
									></v-select>
								</v-col>
								<v-col cols="12">
									<label class="field-label">Description</label>
									<VueEditor
										v-model="editedItem.description"
										:editorToolbar="customToolbar"
										class="mt-2"
									></VueEditor>
								</v-col>
							</v-row>
						</v-card-text>
						<v-card-actions class="pa-4 pt-2">
							<v-btn text color="error" rounded @click="close">Annuler</v-btn>
							<v-spacer></v-spacer>
							<v-btn depressed rounded class="fl-save-btn" @click="save">
								Enregistrer
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</div>

			<v-divider></v-divider>

			<!-- Table -->
			<v-data-table
				:loading="loadingTable"
				loading-text="Chargement en cours..."
				:headers="headers"
				:items="foods"
				:server-items-length="totalItems"
				:options.sync="options"
				class="fl-table"
				:footer-props="{
					'items-per-page-options': [10, 25, 50],
					'items-per-page-text': 'Lignes par page',
					'items-per-page-all-text': 'Tout',
				}"
			>
				<template v-slot:[`item.description`]="{ item }">
					<span class="fl-desc" v-html="item.description"></span>
				</template>

				<template v-slot:[`item.photo`]="{ item }">
					<v-edit-dialog
						:return-value.sync="item.photo"
						large
						@save="updatePhoto(item)"
						save-text="Enregistrer"
						cancel-text="Annuler"
					>
						<img
							crossorigin="anonymous"
							width="52"
							height="52"
							:src="`${server.url}/food/image/${item.photo}`"
							class="fl-food-img"
						/>
						<template v-slot:input>
							<p class="text-subtitle-2 mt-3 mb-2">Image du repas</p>
							<v-file-input
								v-model="photo"
								label="Choisir une image"
								outlined
								dense
								rounded
								hide-details
								prepend-inner-icon="mdi-image"
								prepend-icon=""
							></v-file-input>
						</template>
					</v-edit-dialog>
				</template>

				<template v-slot:[`item.actions`]="{ item }">
					<v-btn icon small @click="editItem(item)">
						<v-icon size="17" color="#374151">mdi-pencil</v-icon>
					</v-btn>
					<v-btn icon small @click="deleteItem(item)">
						<v-icon size="17" color="#c62828">mdi-delete</v-icon>
					</v-btn>
				</template>

				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- Delete dialog -->
		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon">
						<v-icon size="18" color="white">mdi-alert</v-icon>
					</div>
					<p class="del-title">Supprimer cet élément ?</p>
				</div>
				<v-card-text class="pb-1 text-body-2 grey--text text--darken-1">
					Cette action est irréversible.
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="closeDelete">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteItemConfirm">
						Supprimer
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import server from "@/config/address";
import service from "@/services";
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
export default {
	components: { Loader, FloatingAlert },
	data() {
		return {
			search: "",
			searchTimeout: null,
			loadingTable: false,
			options: {},
			totalItems: 0,
			photo: null,
			dialog: false,
			dialogDelete: false,
			loading: false,
			alertMessage: "",
			alertShow: false,
			alertType: "",
			server,
			headers: [
				{ text: "Intitulé", align: "start", value: "label" },
				{ text: "Catégorie", value: "category.label" },
				{ text: "Description", value: "description" },
				{ text: "Photo", value: "photo", sortable: false },
				{ text: "Actions", value: "actions", sortable: false },
			],
			foods: [],
			editedIndex: -1,
			editedItem: { label: "", category: "", description: "" },
			defaultItem: { label: "", category: "", description: "" },
			foodCategories: [],
			customToolbar: [
				[{ header: [false, 1, 2, 3, 4, 5, 6] }],
				["bold", "italic", "underline"],
				[{ color: [] }],
				[{ list: "ordered" }, { list: "bullet" }],
			],
		};
	},
	computed: {
		formTitle() {
			return this.editedIndex === -1 ? "Nouveau repas" : "Modifier un repas";
		},
	},
	watch: {
		options: {
			handler() { this.initialize(); },
			deep: true,
		},
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) {
					this.options.page = 1;
				} else {
					this.initialize();
				}
			}, 400);
		},
		dialog(val) { val || this.close(); },
		dialogDelete(val) { val || this.closeDelete(); },
	},
	created() { this.getFoodCategories(); },
	methods: {
		initialize() {
			this.foods = [];
			this.loadingTable = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getFoodsPaginated({ page, limit, search: this.search }).then((r) => { this.foods = r.data.foods; this.totalItems = r.data.totalPages * limit; this.loadingTable = false; }).catch((e) => { console.log(e); this.loadingTable = false; });
		},
		getFoodCategories() {
			service.getFoodCategories().then((r) => { this.foodCategories = r.data?.categories || r.data || []; }).catch((e) => console.log(e));
		},
		updatePhoto(item) {
			this.loading = true;
			const fd = new FormData();
			fd.append("photo", this.photo);
			fd.append("foodId", item._id);
			service.foodImageUpload(fd).then(() => { this.initialize(); this.loading = false; }).catch((e) => { console.log(e); this.loading = false; });
		},
		editItem(item) { this.editedIndex = this.foods.indexOf(item); this.editedItem = Object.assign({}, item); this.dialog = true; },
		deleteItem(item) { this.editedIndex = this.foods.indexOf(item); this.editedItem = Object.assign({}, item); this.dialogDelete = true; },
		deleteItemConfirm() {
			this.alertShow = false;
			this.loading = true;
			service.deleteFood(this.editedItem._id).then((r) => { this.foods.splice(this.editedIndex, 1); this.closeDelete(); this.loading = false; this.alertMessage = r.data; this.alertShow = true; this.alertType = "green"; }).catch((e) => { this.loading = false; this.alertMessage = e; this.alertShow = true; this.alertType = "red"; });
		},
		close() { this.dialog = false; this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.editedIndex = -1; }); },
		closeDelete() { this.dialogDelete = false; this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.editedIndex = -1; }); },
		save() {
			if (this.editedIndex > -1) {
				this.loading = true;
				service.updateFood(this.editedItem._id, this.editedItem).then(() => { Object.assign(this.foods[this.editedIndex], this.editedItem); this.loading = false; this.close(); this.initialize(); }).catch((e) => { console.log(e); this.loading = false; });
			} else {
				this.loading = true;
				service.createFood(this.editedItem).then(() => { this.close(); this.initialize(); this.loading = false; }).catch((e) => { console.log(e); this.loading = false; });
			}
		},
	},
};
</script>

<style scoped>
.fl-header {
	display: flex;
	align-items: center;
	padding: 18px 20px 14px;
	gap: 14px;
	flex-wrap: wrap;
}
.fl-header-left {
	display: flex;
	align-items: center;
	gap: 14px;
	flex: 1;
	min-width: 200px;
}
.fl-icon-box {
	width: 38px;
	height: 38px;
	border-radius: 10px;
	background: #00695c;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.fl-title {
	font-size: 0.92rem;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
	font-family: Montserrat, sans-serif;
}
.fl-subtitle {
	font-size: 0.72rem;
	color: #9ca3af;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.fl-search {
	max-width: 260px;
}
.fl-add-btn {
	background: #c62828 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
.fl-table {
	font-family: Montserrat, sans-serif;
}
.fl-desc {
	font-size: 0.78rem;
	color: #6b7280;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}
.fl-food-img {
	border-radius: 8px;
	object-fit: cover;
	cursor: pointer;
}
.fl-dialog-header {
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 18px 20px 14px;
}
.fl-icon-box-sm {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	background: #00695c;
	display: flex;
	align-items: center;
	justify-content: center;
}
.fl-dialog-title {
	font-size: 0.88rem;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
	font-family: Montserrat, sans-serif;
}
.fl-dialog-sub {
	font-size: 0.68rem;
	color: #9ca3af;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.field-label {
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	font-family: Montserrat, sans-serif;
}
.fl-save-btn {
	background: #1565c0 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
.del-header {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px 20px 12px;
}
.del-icon {
	width: 34px;
	height: 34px;
	border-radius: 8px;
	background: #c62828;
	display: flex;
	align-items: center;
	justify-content: center;
}
.del-title {
	font-size: 0.9rem;
	font-weight: 700;
	color: #111827;
	margin: 0;
	font-family: Montserrat, sans-serif;
}
.del-confirm-btn {
	background: #c62828 !important;
	color: white !important;
	font-family: Montserrat, sans-serif;
	font-weight: 700;
	font-size: 0.82rem;
}
</style>

